import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { BlightModule } from '@next/arable-lib/blight';
import { CropModule } from '@next/arable-lib/crop';
import { GeoModule } from '@next/arable-lib/geo';
import { DirectivesModule } from '@next/arable-lib/shared';
import {
  AgrLogoWideModule,
  AgrNotificationEmptyModule,
  AgrPageDetailModule,
  AgrPageModule,
  AgrPageSidesheetModule,
  AgrPageToolbarModule,
  AgrPrintPageModule,
} from '@next/core-lib/components';
import { AgrDialogModule } from '@next/core-lib/dialog';
import { AgrFormModule } from '@next/core-lib/form';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';

import { CopyCropFieldsToManureSpaceModule } from '../copy-cropfields-to-manure-space/copy-cropfields-to-manure-space.module';
import { GeoModule as InternalGeoModule } from '../geo/geo.module';
import { SalesPotentialDialogComponent } from '../sales-potential/components/sales-potential-dialog/sales-potential-dialog.component';
import { SalesPotentialEffects } from '../sales-potential/effects/sales-potential.effects';
import { salesPotentialReducer } from '../sales-potential/reducers/sales-potential.reducer';
import { SalesPotentialComponent } from '../sales-potential/sales-potential.component';
import { SharedModule } from '../shared/shared.module';

import { SoilAnalysisModule } from '../soil-analysis/soil-analysis.module';
import { CropfieldDetailComponent } from './components/cropfield-detail/cropfield-detail.component';
import { CropFieldDialogComponent } from './components/cropfield-dialog/cropfield-dialog.component';
import { CropFieldListComponent } from './components/cropfield-list/cropfield-list.component';
import { CropFieldSearchComponent } from './components/cropfield-search/cropfield-search.component';
import { EditMachiningDepthDialogComponent } from './components/edit-machining-depth-dialog/edit-machining-depth-dialog.component';
import { FmsDiagnoseCropFieldsTabComponent } from './components/fms-diagnose-dialog/fms-diagnose-crop-fields-tab.component';
import { FmsDiagnoseDialogComponent } from './components/fms-diagnose-dialog/fms-diagnose-dialog.component';
import { FmsDiagnoseLinkageTabComponent } from './components/fms-diagnose-dialog/fms-diagnose-linkage-tab.component';
import { FmsDiagnoseMapComponent } from './components/fms-diagnose-dialog/fms-diagnose-map.component';
import { FmsDiagnoseSpraysTabComponent } from './components/fms-diagnose-dialog/fms-diagnose-sprays-tab.component';
import { ImportCropfieldsDialogComponent } from './components/import-cropfields-dialog/import-cropfields-dialog.component';
import { OverviewDialogComponent } from './components/overview-dialog/overview-dialog.component';

import { ProtectedFieldsSkippedDialogComponent } from './components/protected-fields-skipped-dialog/protected-fields-skipped-dialog.component';
import { QuickEditDialogComponent } from './components/quick-edit-dialog/quick-edit-dialog.component';
import { RetrieveCropfieldsDialogComponent } from './components/retrieve-cropfields-dialog/retrieve-cropfields-dialog.component';
import { SyncCropfieldsDialogComponent } from './components/sync-cropfields-dialog/sync-cropfields-dialog.component';
import { SyncCropfieldsDialogV2Component } from './components/sync-cropfields-dialog-v2/sync-cropfields-dialog-v2.component';
import { SyncCropfieldsFailedDialogComponent } from './components/sync-cropfields-failed-dialog/sync-cropfields-failed-dialog.component';
import { RegistrationComponent } from './registration.component';
import { CropFieldNotificationEffects } from './store/effects/crop-field-notification.effects';
import { CropFieldEffects } from './store/effects/crop-field.effects';

import { CropVarietyEffects } from './store/effects/crop-variety.effects';
import { CropEffects } from './store/effects/crop.effects';
import { ProtectedFieldsSkippedNotificationEffects } from './store/effects/protected-fields-skipped-notification.effects';
import { SoilTypeEffects } from './store/effects/soil-type.effects';
import { cropFieldReducer } from './store/reducers/crop-field.reducer';
import { cropVarietyReducer } from './store/reducers/crop-variety.reducer';
import { mostUsedCropReducer } from './store/reducers/most-used-crop.reducer';
import { soilTypeReducer } from './store/reducers/soil-type.reducer';

@NgModule({
  declarations: [
    RegistrationComponent,
    CropFieldListComponent,
    CropFieldDialogComponent,
    CropFieldSearchComponent,
    OverviewDialogComponent,
    EditMachiningDepthDialogComponent,
    FmsDiagnoseDialogComponent,
    FmsDiagnoseLinkageTabComponent,
    FmsDiagnoseCropFieldsTabComponent,
    FmsDiagnoseMapComponent,
    FmsDiagnoseSpraysTabComponent,
    QuickEditDialogComponent,
    CropfieldDetailComponent,
    RetrieveCropfieldsDialogComponent,
    ImportCropfieldsDialogComponent,
    SalesPotentialComponent,
    SalesPotentialDialogComponent,
    SyncCropfieldsDialogComponent,
    SyncCropfieldsDialogV2Component,
    SyncCropfieldsFailedDialogComponent,
    ProtectedFieldsSkippedDialogComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatCheckboxModule,
    MatListModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatSlideToggleModule,
    MatInputModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatExpansionModule,
    MatTabsModule,
    MatTooltipModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatTableModule,
    AgrPageSidesheetModule,
    AgrNotificationEmptyModule,
    AgrDialogModule,
    AgrFormModule,
    SharedModule,
    GeoModule,
    InternalGeoModule,
    BlightModule,
    SoilAnalysisModule,
    DragDropModule,
    RouterModule,
    AgrLogoWideModule,
    AgrPageDetailModule,
    AgrPrintPageModule,
    CopyCropFieldsToManureSpaceModule,
    CropModule,
    MatPaginatorModule,
    DirectivesModule,
    MatRippleModule,
    AgrPageModule,
    AgrPageToolbarModule,
  ],
  providers: [
    provideEffects([
      CropEffects,
      CropVarietyEffects,
      CropFieldEffects,
      SoilTypeEffects,
      CropFieldNotificationEffects,
      ProtectedFieldsSkippedNotificationEffects,
      SalesPotentialEffects,
    ]),
    provideState('most-used-crops', mostUsedCropReducer),
    provideState('crop-varieties-by-crop-id', cropVarietyReducer),
    provideState('crop-field', cropFieldReducer),
    provideState('soil-type', soilTypeReducer),
    provideState('salesPotential', salesPotentialReducer),
  ],
})
export class RegistrationModule {}
